import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

/**
 * Individual Toast Component
 * Beautiful, animated toast notifications with auto-dismiss
 */
export default function Toast({ id, type = 'info', message, duration = 4000, onClose }) {
  const [isExiting, setIsExiting] = useState(false)
  const [progress, setProgress] = useState(100)

  useEffect(() => {
    // Auto-dismiss timer
    const dismissTimer = setTimeout(() => {
      handleClose()
    }, duration)

    // Progress bar animation
    const startTime = Date.now()
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100)
      setProgress(remaining)
    }, 50)

    return () => {
      clearTimeout(dismissTimer)
      clearInterval(progressInterval)
    }
  }, [duration])

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(() => {
      onClose(id)
    }, 300) // Match animation duration
  }

  const variants = {
    success: {
      bg: 'bg-green-500/90',
      icon: CheckCircle,
      iconColor: 'text-white',
      progressBar: 'bg-green-300'
    },
    error: {
      bg: 'bg-red-500/90',
      icon: XCircle,
      iconColor: 'text-white',
      progressBar: 'bg-red-300'
    },
    warning: {
      bg: 'bg-yellow-500/90',
      icon: AlertTriangle,
      iconColor: 'text-white',
      progressBar: 'bg-yellow-300'
    },
    info: {
      bg: 'bg-blue-500/90',
      icon: Info,
      iconColor: 'text-white',
      progressBar: 'bg-blue-300'
    }
  }

  const variant = variants[type] || variants.info
  const Icon = variant.icon

  return (
    <div
      className={`
        ${variant.bg} backdrop-blur-sm text-white
        px-4 py-3 rounded-lg shadow-2xl
        flex items-center gap-3
        min-w-[320px] max-w-[420px]
        transform transition-all duration-300 ease-out
        ${isExiting
          ? 'translate-x-[120%] opacity-0'
          : 'translate-x-0 opacity-100 animate-slide-in-right'
        }
      `}
      role="alert"
    >
      {/* Icon */}
      <Icon className={`w-5 h-5 flex-shrink-0 ${variant.iconColor}`} />

      {/* Message */}
      <span className="flex-1 text-sm font-medium leading-snug">{message}</span>

      {/* Close Button */}
      <button
        onClick={handleClose}
        className="flex-shrink-0 hover:bg-white/20 rounded p-1 transition-colors"
        aria-label="Close"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 rounded-b-lg overflow-hidden">
        <div
          className={`h-full ${variant.progressBar} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}
